/**
 * Admin: set up an envelope from a pattern. A live preview shows the envelope
 * exactly as members will see it — label, stamp and flap — and a slider sets
 * how large the pattern is drawn.
 */

import { useState } from 'react';
import type { Stationery } from '../letters/api';
import { Envelope } from '../stationery/Envelope';
import { PostageStamp } from '../postage/Stamp';

export interface EnvelopeDraft {
  name: string;
  scale: number;
}

export function EnvelopeEditor({
  imageUrl,
  width,
  height,
  initial,
  saving,
  saveLabel,
  onSave,
  onCancel,
}: {
  imageUrl: string;
  width: number;
  height: number;
  initial: EnvelopeDraft;
  saving: boolean;
  saveLabel: string;
  onSave: (d: EnvelopeDraft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [scale, setScale] = useState(initial.scale);
  const preview: Stationery = {
    id: 'preview',
    kind: 'envelope',
    name: name || 'Envelope',
    imageUrl,
    width,
    height,
    area: { top: 0, right: 0, bottom: 0, left: 0 },
    ink: '#2C2420',
    scale,
  };
  return (
    <div className="paper-editor envelope-editor">
      <p className="admin-setting__label">How it looks</p>
      <div className="envelope-editor__preview">
        <Envelope envelope={preview} to="To @maya" from="from @leon" stamp={<PostageStamp cls="second" size="sheet" />} />
        <Envelope envelope={preview} to="To @maya" from="from @leon" stamp={<PostageStamp cls="second" size="medium" />} compact />
      </div>
      <div className="admin-stamps__new">
        <label className="admin-setting__input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Jazz night" />
        </label>
        <label className="admin-setting__input admin-stamps__grow">
          <span>Pattern size</span>
          <input
            type="range"
            min={0.25}
            max={3}
            step={0.05}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            aria-label="Pattern size"
          />
          <span>{Math.round(scale * 100)}%</span>
        </label>
      </div>
      <div className="admin-stamps__actions">
        <button type="button" className="btn btn--primary" disabled={saving || !name.trim()} onClick={() => onSave({ name: name.trim(), scale })}>
          {saving ? 'Saving…' : saveLabel}
        </button>
        <button type="button" className="btn btn--ghost" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
